import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Edit as EditIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';

const CategoryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: config } = useSelector((state) => state.config);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`${config.API_BASE_URL}/api/categories/${id}`);
        setCategory(response.data);
      } catch (error) {
        console.error('Error fetching category:', error);
        setError('Failed to load category details');
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [id, config.API_BASE_URL]);

  const handleEdit = () => {
    navigate(`/categories/edit/${id}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!category) {
    return (
      <Box p={3}>
        <Typography>Category not found</Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} style={{ marginRight: '10px' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">Category Details</Typography>
        <Box flexGrow={1} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          Edit Category
        </Button>
      </Box>

      <Paper elevation={3} style={{ padding: '20px', marginBottom: '20px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <Divider style={{ marginBottom: '15px' }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                <Typography variant="body1" gutterBottom>{category.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Sort Order</Typography>
                <Typography variant="body1" gutterBottom>{category.sortOrder || 0}</Typography>
              </Grid>
            </Grid>

            {category.icon && (
              <Box mt={3}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Icon
                </Typography>
                <img 
                  src={`${config.API_BASE_URL}/images/${category.icon}`} 
                  alt={category.name} 
                  style={{ 
                    maxWidth: '100px', 
                    maxHeight: '100px',
                    objectFit: 'contain' 
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Price Ranges</Typography>
            <Divider style={{ marginBottom: '15px' }} />
            
            {category.priceRanges && category.priceRanges.length > 0 ? (
              <Grid container spacing={2}>
                {category.priceRanges.map((range, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper variant="outlined" style={{ padding: '10px' }}>
                      <Typography variant="subtitle2">{range.label}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        ₹{range.min.toLocaleString()} - ₹{range.max.toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No price ranges defined for this category.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Box mt={3}>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
        >
          Back to Categories
        </Button>
      </Box>
    </Box>
  );
};

export default CategoryDetails;
